import { AnimationAnimateMetadata, AnimationBuilder, AnimationStyleMetadata } from '@angular/animations';
import {
  AfterViewInit,
  ContentChild,
  Directive,
  ElementRef,
  Input,
  Renderer2,
  inject,
  DestroyRef,
  HostListener
} from '@angular/core';
import { AnimationDirection } from './label-animation.directive.enum';
import { LanguageDirection } from '@pc/translation';
import { initAnimations, labelStyleInit } from './label-animation.animation';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { timer } from 'rxjs';

// Instructions For Using This Directive:
// 1. Mention the 'pcLabelUpInputAnimation' directive in the wanted pressed element.
// 2. Mention the '#labelUpDirective' selector in the element (label) that should be animated up and down.
// 3. Mention the '#textDirective' selector in the input element.
// 4. In order to get the 'focus' and 'blur' events detection, do not forget to put 'tabIndex' on the input element.

// Nore: this directive's animation can be activated by components themselves:
// 1. Mention the 'pcLabelUpInputAnimation' directive in the wanted pressed element.
// 2. Mention the '#labelUpDirective' selector in the element (label) that should be animated up and down.
// 3. Use the 'approveContentAnimation', 'clearContentAnimation', 'initiatedDateCase' functions from component's ts file.

@Directive({
  selector: '[pcLabelUpInputAnimation]',
  standalone: true
})
export class LabelAnimationDirective implements AfterViewInit {
  @ContentChild('labelUpDirective') public labelChild!: ElementRef;
  @ContentChild('textDirective') public inputChild!: ElementRef;
  @Input() alwaysTop = false;

  public el = inject(ElementRef);
  public animationBuilder = inject(AnimationBuilder);
  public renderer = inject(Renderer2);
  public prevInputText!: string;
  public isPrevClickWasInside = false;
  protected destroyRef = inject(DestroyRef);
  private toTopAnimation!: (AnimationStyleMetadata | AnimationAnimateMetadata)[];
  private toBottomAnimation!: (AnimationStyleMetadata | AnimationAnimateMetadata)[];

  @HostListener('keyup', ['$event'])
  onKeyUp(event: KeyboardEvent): void {
    if (
      (this.inputChild?.nativeElement.value !== '' && event.ctrlKey && event.key.toLowerCase() === 'y') ||
      (event.ctrlKey && event.shiftKey && event.key.toLowerCase() === 'z')
    ) {
      this.animateLabel();
    }
  }

  ngAfterViewInit() {
    if (this.inputChild) {
      this.initiatedDateCase();
      this.initInputAndLabelConfig();
      this.animateIfValue();
    }
  }

  public animateIfValue(): void {
    timer(1)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const inputTextValue = this.inputChild.nativeElement.value;
        if (inputTextValue) {
          this.animateLabel();
        }
      });
  }

  public approveContentAnimation(): void {
    this.animateLabel();
  }

  public clearContentAnimation(): void {
    this.animateLabel(false);
  }

  public onInputChange(): void {
    const inputText = this.inputChild?.nativeElement.value;
    if (this.shouldOutOfElementFocus(inputText)) {
      this.animateLabel(false);
    }
    this.prevInputText = inputText;
  }

  public onInputBlur(): void {
    const inputText = this.inputChild?.nativeElement.value;
    if (inputText?.trim().length === 0 && this.isPrevClickWasInside) {
      this.animateLabel(false);
    }
    this.isPrevClickWasInside = false;
  }

  public onInputFocus(): void {
    const inputText = this.inputChild?.nativeElement.value;
    if (inputText?.trim().length === 0 && !this.isPrevClickWasInside) {
      this.animateLabel();
    }
    this.isPrevClickWasInside = true;
  }

  public initInputAndLabelConfig(): void {
    this.renderer.listen(this.inputChild?.nativeElement, 'input', () => {
      this.onInputChange();
    });
    this.renderer.listen('document', 'click', (event) => {
      if (!this.el.nativeElement.contains(event.target) && !this.inputChild?.nativeElement.contains(event.target)) {
        // The click is outside the item
        this.onInputBlur();
      } else {
        // The click is on the item
        this.onInputFocus();
      }
    });
    this.renderer.listen(this.inputChild?.nativeElement, 'focus', () => {
      this.onInputFocus();
    });
    this.renderer.listen(this.inputChild?.nativeElement, 'blur', () => {
      this.onInputBlur();
    });
  }

  public animateLabel(isStart = true): void {
    if (this.alwaysTop) return;

    this.animationParamsInit(false, isStart);
  }

  public initiatedDateCase(element: ElementRef = this.el): void {
    this.animationParamsInit(true, false, element);
  }

  public animationParamsInit(isOnInit = false, isStart = false, element: ElementRef = this.el) {
    const div = this.labelChild ? this.labelChild.nativeElement : this.el.nativeElement;
    // Can not use 'Directionality => value' because it does not recognize the document direction
    const originDir =
      getComputedStyle(div).direction === LanguageDirection.LTR ? AnimationDirection.LEFT : AnimationDirection.RIGHT;

    if (isOnInit) {
      const inputText = this.inputChild ? this.inputChild.nativeElement?.value : element?.nativeElement?.textContent;
      if (inputText?.trim().length > 0) {
        labelStyleInit(originDir, this.animationBuilder, div);
      }
    } else {
      initAnimations(originDir, this.toTopAnimation, this.toBottomAnimation, this.animationBuilder, isStart, div);
    }
  }

  private shouldOutOfElementFocus(inputText: string): boolean {
    return (
      inputText?.trim().length === 0 &&
      this.prevInputText?.length === 0 &&
      document.activeElement !== this.inputChild?.nativeElement
    );
  }
}
